import * as React from "react"

// ** Third Party Components
import { DayPicker } from "react-day-picker"
import { ArrowLeft2, ArrowRight2 } from "iconsax-reactjs"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Theme Element
import { buttonVariants } from "./button"

export type CalendarElementProps = React.ComponentProps<typeof DayPicker>

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  )

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile(e.matches)
    handler(mql)
    mql.addEventListener("change", handler)
    return () => mql.removeEventListener("change", handler)
  }, [breakpoint])

  return isMobile
}

function CalendarElement({
  className,
  classNames,
  showOutsideDays = true,
  numberOfMonths,
  ...props
}: CalendarElementProps) {
  const isMobile = useIsMobile()

  // On mobile, show only 1 calendar month even when range mode requests 2
  const resolvedNumberOfMonths =
    isMobile && props.mode === "range" ? 1 : numberOfMonths

  return (
    <DayPicker
      numberOfMonths={resolvedNumberOfMonths}
      showOutsideDays={showOutsideDays}
      className={cn("rounded-lg bg-gray-50 p-4 shadow-lg", className)} // Adding background color and rounding
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0", // Layout of months
        month: "space-y-4", // Space between months
        caption: "flex justify-center pt-1 relative items-center text-lg font-bold text-gray-700", // Caption styling
        caption_label: "text-sm font-medium text-gray-600", // Caption label color
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-2 opacity-75 hover:opacity-100 focus:outline-none transition-all duration-300 ease-in-out hover:bg-gray-200 rounded-full",
        ), // Adding hover effects to navigation buttons
        table: "w-full border-collapse space-y-1", // Table layout and spacing
        head_row: "flex bg-gray-100 rounded-md", // Row styling
        head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] text-gray-500", // Column headers
        row: "flex w-full mt-2", // Row spacing
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 transition-all duration-200", // Adding transition for smooth hover effects
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md",
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal aria-selected:opacity-100 transition-all duration-200 ease-in-out hover:bg-indigo-100 hover:text-indigo-600 focus:bg-indigo-600 focus:text-white",
        ), // Day button with hover effects
        day_range_start: "day-range-start !bg-indigo-500 !text-white",
        day_range_end: "day-range-end !bg-indigo-500 !text-white",
        day_selected: "bg-indigo-500 text-white hover:bg-indigo-700 focus:bg-indigo-700",
        day_today: "bg-accent text-accent-foreground border-2 border-indigo-500 bg-indigo-500", // Highlight current day
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
        day_range_middle: "aria-selected:bg-indigo-100 rounded-none", // Range middle days styling
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ArrowLeft2 className='h-5 w-5 text-indigo-600 hover:text-indigo-700' />, // Styling navigation icon
        IconRight: () => <ArrowRight2 className='h-5 w-5 text-indigo-600 hover:text-indigo-700' />, // Styling navigation icon
      }}
      {...props}
    />
  )
}
CalendarElement.displayName = "Calendar"

export { CalendarElement }
